/**
 * Dashboard — 30-day cash flow chart.
 *
 * Inflows/outflows as bars (positive/negative around zero) with a net line
 * overlay. Outflows are passed as negative values so the bars naturally
 * appear below the zero line.
 */

import { useEffect, useState } from 'react'

import { Card, Skeleton } from '@elsapiens/ui'
import {
  Bar, CartesianGrid, ComposedChart, Legend, Line,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'

import { getDailyCashFlow, type DailyCashFlowPoint } from '../../services/period-end-service'
import { useOrganization } from '../../stores/organization-store'
import { useSettings } from '../../stores/settings-store'
import { formatAmount, toLocalDateStr } from '../../utils/formatters'

const RANGE_DAYS = 30

// SVG-safe chart palette. Recharts renders into SVG so it can't read CSS
// custom properties; we resolve a hex per theme by watching the `dark` class
// on <html> (mirrors the elSDK chart pattern, which isn't exported for us).
interface ChartPalette {
  axis: string       // axis tick labels
  grid: string       // grid line stroke
  tooltipBg: string
  tooltipBorder: string
  tooltipFg: string
  tooltipFgMuted: string
  inflow: string
  outflow: string
  net: string
}

const LIGHT_PALETTE: ChartPalette = {
  axis: '#737373', grid: '#e5e5e5',
  tooltipBg: '#ffffff', tooltipBorder: '#e5e5e5',
  tooltipFg: '#171717', tooltipFgMuted: '#737373',
  inflow: '#16a34a', outflow: '#dc2626', net: '#2563eb',
}
const DARK_PALETTE: ChartPalette = {
  axis: '#a3a3a3', grid: '#333333',
  tooltipBg: '#1f1f1f', tooltipBorder: '#333333',
  tooltipFg: '#f5f5f5', tooltipFgMuted: '#a3a3a3',
  inflow: '#4ade80', outflow: '#f87171', net: '#60a5fa',
}

function isDarkMode(): boolean {
  if (typeof document === 'undefined') { return false }
  return document.documentElement.classList.contains('dark')
}

function useChartPalette(): ChartPalette {
  const [palette, setPalette] = useState<ChartPalette>(() => (isDarkMode() ? DARK_PALETTE : LIGHT_PALETTE))
  useEffect(() => {
    const update = (): void => { setPalette(isDarkMode() ? DARK_PALETTE : LIGHT_PALETTE) }
    const observer = new MutationObserver(muts => {
      for (const m of muts) {
        if (m.attributeName === 'class') { update(); break }
      }
    })
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])
  return palette
}

interface ChartPoint {
  date: string         // YYYY-MM-DD
  label: string        // short tick label (e.g. "12 May")
  inflow: number
  outflow: number      // negative for chart positioning
  net: number
}

function rangeDates(): { start: string; end: string } {
  const end = new Date()
  const start = new Date()
  start.setDate(end.getDate() - (RANGE_DAYS - 1))
  return { start: toLocalDateStr(start), end: toLocalDateStr(end) }
}

function toChartPoint(p: DailyCashFlowPoint): ChartPoint {
  // Display "DD MMM" — short enough to fit 30 ticks on the x-axis.
  const d = new Date(`${p.date}T00:00:00`)
  const label = d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' })
  return {
    date: p.date,
    label,
    inflow: p.inflow,
    outflow: -p.outflow, // flip sign so outflow bars sit below zero
    net: p.net,
  }
}

function ChartTooltip({ active, payload, currency, palette }: {
  active?: boolean
  payload?: Array<{ payload: ChartPoint }>
  currency: string
  palette: ChartPalette
}): JSX.Element | null {
  if (!active || !payload || payload.length === 0) { return null }
  const p = payload[0]?.payload
  if (!p) { return null }
  return (
    <div
      className="rounded-md el-p-sm shadow-sm text-xs"
      style={{
        background: palette.tooltipBg,
        border: `1px solid ${palette.tooltipBorder}`,
        color: palette.tooltipFg,
      }}
    >
      <div className="font-semibold el-mb-xs">{p.label}</div>
      <div className="flex justify-between gap-3">
        <span style={{ color: palette.tooltipFgMuted }}>Inflow</span>
        <span className="font-mono" style={{ color: palette.inflow }}>{formatAmount(p.inflow, currency)}</span>
      </div>
      <div className="flex justify-between gap-3">
        <span style={{ color: palette.tooltipFgMuted }}>Outflow</span>
        <span className="font-mono" style={{ color: palette.outflow }}>{formatAmount(-p.outflow, currency)}</span>
      </div>
      <div className="flex justify-between gap-3 el-pt-xs el-mt-xs" style={{ borderTop: `1px solid ${palette.tooltipBorder}` }}>
        <span style={{ color: palette.tooltipFgMuted }}>Net</span>
        <span className="font-mono" style={{ color: p.net < 0 ? palette.outflow : palette.inflow }}>{formatAmount(p.net, currency)}</span>
      </div>
    </div>
  )
}

interface CashFlowChartContentProps {
  data: ChartPoint[]
  currency: string
  palette: ChartPalette
}

function CashFlowChartContent({ data, currency, palette }: CashFlowChartContentProps): JSX.Element {
  return (
    <ResponsiveContainer height={260} width="100%">
      <ComposedChart data={data} margin={{ top: 8, right: 12, left: 8, bottom: 4 }}>
        <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="label"
          interval="preserveStartEnd"
          minTickGap={28}
          stroke={palette.axis}
          tick={{ fontSize: 11, fill: palette.axis }}
        />
        <YAxis
          stroke={palette.axis}
          tick={{ fontSize: 11, fill: palette.axis }}
          tickFormatter={(v: number) => formatAmount(v, currency)}
          width={70}
        />
        <Tooltip content={<ChartTooltip currency={currency} palette={palette} />} cursor={{ fill: palette.grid, opacity: 0.3 }} />
        <Legend wrapperStyle={{ fontSize: 12, color: palette.tooltipFg }} />
        <Bar dataKey="inflow" fill={palette.inflow} name="Inflow" radius={[2, 2, 0, 0]} />
        <Bar dataKey="outflow" fill={palette.outflow} name="Outflow" radius={[0, 0, 2, 2]} />
        <Line dataKey="net" dot={false} name="Net" stroke={palette.net} strokeWidth={2} type="monotone" />
      </ComposedChart>
    </ResponsiveContainer>
  )
}

export function CashFlowChart(): JSX.Element {
  const { organizationId } = useOrganization()
  const { settings } = useSettings()
  const palette = useChartPalette()
  const [points, setPoints] = useState<ChartPoint[] | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!organizationId) { return }
    let isCancelled = false
    setIsLoading(true)
    const { start, end } = rangeDates()
    void getDailyCashFlow(organizationId, start, end)
      .then(res => {
        if (isCancelled) { return }
        if (res.data) {
          setPoints(res.data.points.map(toChartPoint))
          setError(null)
        } else {
          setError(res.error?.message ?? 'Failed to load cash flow')
        }
      })
      .catch(() => {
        if (!isCancelled) { setError('Failed to load cash flow') }
      })
      .finally(() => {
        if (!isCancelled) { setIsLoading(false) }
      })
    return () => { isCancelled = true }
  }, [organizationId])

  const hasActivity = points?.some(p => p.inflow !== 0 || p.outflow !== 0)

  return (
    <Card className="el-mt-lg el-mb-lg">
      <div className="flex items-baseline justify-between el-mb-sm">
        <h3 className="text-base font-semibold text-foreground">Cash Flow — Last 30 days</h3>
        <span className="text-xs text-foreground-secondary">Inflows · Outflows · Net</span>
      </div>
      <CashFlowChartBody
        currency={settings.baseCurrency}
        error={error}
        hasActivity={!!hasActivity}
        isLoading={isLoading}
        palette={palette}
        points={points}
      />
    </Card>
  )
}

interface CashFlowChartBodyProps {
  isLoading: boolean
  error: string | null
  hasActivity: boolean
  points: ChartPoint[] | null
  currency: string
  palette: ChartPalette
}

function CashFlowChartBody({ isLoading, error, hasActivity, points, currency, palette }: CashFlowChartBodyProps): JSX.Element {
  if (isLoading) { return <Skeleton height="260px" width="100%" /> }
  if (error) { return <p className="text-sm text-red-600 dark:text-red-400 py-8 text-center">{error}</p> }
  if (!hasActivity) { return <p className="text-sm text-foreground-secondary py-12 text-center">No cash activity in the last 30 days.</p> }
  return <CashFlowChartContent currency={currency} data={points ?? []} palette={palette} />
}
